(() => {
  if (window.WubCollectionV2) return;

  const DEPTH_PARAM = 'wub_page';
  const POSITION_PREFIX = 'wub:collection-position:';
  const POSITION_MAX_AGE = 30 * 60 * 1000;

  const state = {
    aborters: new WeakMap(),
    infiniteObservers: new WeakMap(),
    restorePromises: new WeakMap(),
    scrollTicking: false
  };

  const qs = (root, selector) => root ? root.querySelector(selector) : null;
  const qsa = (root, selector) => root ? Array.from(root.querySelectorAll(selector)) : [];
  const getRoot = (node) => node && node.closest ? node.closest('[data-wub-collection]') : null;
  const collectionText = (key, fallback = '') => window.WUB?.i18n?.collection?.[key] || fallback;

  function cleanSectionUrl(rawUrl, sectionId) {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function publicUrl(rawUrl) {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('section_id');
    return url.pathname + url.search + url.hash;
  }

  function resetBrowseDepth(rawUrl) {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.delete(DEPTH_PARAM);
    url.searchParams.delete('page');
    return url.toString();
  }

  function browseBaseUrl(rawUrl = window.location.href) {
    return resetBrowseDepth(rawUrl);
  }

  function getDepth(rawUrl = window.location.href) {
    const url = new URL(rawUrl, window.location.origin);
    const parsed = parseInt(url.searchParams.get(DEPTH_PARAM) || '1', 10);
    if (!Number.isFinite(parsed)) return 1;
    return Math.max(1, Math.min(parsed, 20));
  }

  function pageUrl(rawUrl, page) {
    const url = new URL(browseBaseUrl(rawUrl), window.location.origin);
    if (page > 1) url.searchParams.set('page', String(page));
    return url.toString();
  }

  function depthStateUrl(depth) {
    const url = new URL(browseBaseUrl(window.location.href), window.location.origin);
    if (depth > 1) url.searchParams.set(DEPTH_PARAM, String(depth));
    return url.toString();
  }

  function positionStorageKey(rawUrl = window.location.href) {
    return POSITION_PREFIX + publicUrl(rawUrl);
  }

  function saveBrowsePosition(root) {
    if (!root) return;
    try {
      sessionStorage.setItem(positionStorageKey(), JSON.stringify({ y: window.scrollY, ts: Date.now() }));
    } catch (_) {}
  }

  function restoreBrowsePosition() {
    let stored = null;
    try {
      stored = sessionStorage.getItem(positionStorageKey());
      if (stored) sessionStorage.removeItem(positionStorageKey());
    } catch (_) {}
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);
      if (!Number.isFinite(parsed.y) || !Number.isFinite(parsed.ts)) return;
      if (Date.now() - parsed.ts > POSITION_MAX_AGE) return;
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: parsed.y, behavior: 'auto' })));
    } catch (_) {}
  }

  function parseIncomingRoot(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-wub-collection]');
  }

  function announce(root, message) {
    const status = qs(root, '[data-wub-collection-status]');
    if (status) status.textContent = message;
  }

  function setLoading(root, loading) {
    root.dataset.loading = loading ? 'true' : 'false';
    root.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function unlockBody() {
    if (!document.querySelector('[data-wub-collection][data-filters-open="true"]')) {
      document.documentElement.classList.remove('wub-filters-lock');
      document.body.classList.remove('wub-filters-lock');
    }
  }

  function closeFilters(root) {
    if (!root) return;
    root.removeAttribute('data-filters-open');
    unlockBody();
    const opener = qs(root, '[data-wub-filter-open]');
    if (opener) opener.setAttribute('aria-expanded', 'false');
  }

  function openFilters(root) {
    if (!root) return;
    root.dataset.filtersOpen = 'true';
    root.setAttribute('data-filters-open', 'true');
    document.documentElement.classList.add('wub-filters-lock');
    document.body.classList.add('wub-filters-lock');
    const opener = qs(root, '[data-wub-filter-open]');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    const first = qs(root, '.wub-facets__close, .wub-facets input:not([disabled])');
    if (first) requestAnimationFrame(() => first.focus({ preventScroll: true }));
  }

  function initInfinite(root) {
    const existing = state.infiniteObservers.get(root);
    if (existing) existing.disconnect();

    const sentinel = qs(root, '[data-wub-infinite-sentinel]');
    const next = qs(root, '[data-wub-load-more]');
    if (!sentinel || !next || root.dataset.paginationType !== 'infinite' || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) loadMore(root, next);
    }, { rootMargin: '500px 0px' });
    observer.observe(sentinel);
    state.infiniteObservers.set(root, observer);
  }

  async function appendPage(root, rawUrl, options = {}) {
    const sectionId = root?.dataset.sectionId;
    const currentGrid = qs(root, '[data-wub-product-grid]');
    const trigger = options.trigger || qs(root, '[data-wub-load-more]');
    if (!root || !sectionId || !currentGrid) throw new Error('Product grid not found');

    if (trigger && options.busy !== false) trigger.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(cleanSectionUrl(rawUrl, sectionId), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const incoming = parseIncomingRoot(html);
      const incomingGrid = qs(incoming, '[data-wub-product-grid]');
      if (!incoming || !incomingGrid) throw new Error('Incoming product grid not found');

      const fragment = document.createDocumentFragment();
      Array.from(incomingGrid.children).forEach(child => fragment.appendChild(child));
      currentGrid.appendChild(fragment);

      const incomingNext = qs(incoming, '[data-wub-load-more]');
      const currentNext = qs(root, '[data-wub-load-more]');
      if (incomingNext && currentNext) {
        currentNext.href = incomingNext.href;
        currentNext.removeAttribute('aria-busy');
      } else if (!incomingNext) {
        const pagination = qs(root, '[data-wub-load-more-wrap]');
        if (pagination) pagination.remove();
      }

      return { incoming, incomingNext };
    } finally {
      if (trigger?.isConnected) trigger.removeAttribute('aria-busy');
    }
  }

  async function restoreLoadedDepth(root) {
    if (!root || root.dataset.paginationType !== 'loadmore') return;
    const targetDepth = getDepth();
    const currentDepth = Math.max(1, parseInt(root.dataset.loadedDepth || '1', 10) || 1);
    if (targetDepth <= currentDepth) return;

    const existing = state.restorePromises.get(root);
    if (existing) return existing;

    const promise = (async () => {
      root.dataset.restoringDepth = 'true';
      let restoredDepth = currentDepth;
      try {
        for (let page = currentDepth + 1; page <= targetDepth; page += 1) {
          const result = await appendPage(root, pageUrl(window.location.href, page), { busy: false });
          restoredDepth = page;
          root.dataset.loadedDepth = String(restoredDepth);
          if (!result.incomingNext && page < targetDepth) break;
        }
        initInfinite(root);
        document.dispatchEvent(new CustomEvent('wub:collection-updated', { detail: { sectionId: root.dataset.sectionId, restoredDepth } }));
      } catch (error) {
        console.error('[WUB Collection V2 restore]', error);
      } finally {
        delete root.dataset.restoringDepth;
        state.restorePromises.delete(root);
      }
    })();

    state.restorePromises.set(root, promise);
    return promise;
  }

  async function restoreBrowseState(root) {
    await restoreLoadedDepth(root);
    restoreBrowsePosition();
  }

  function initRoot(root, options = {}) {
    if (!root) return;
    closeFilters(root);
    if (!root.dataset.loadedDepth) root.dataset.loadedDepth = '1';
    initInfinite(root);
    updateStickyActions();
    if (options.restore !== false) restoreBrowseState(root);
  }

  function syncRootData(root, incoming) {
    const keys = ['paginationType', 'compactCollection', 'collectionHandle', 'collectionId', 'collectionFamily', 'activeFilterCount', 'resultCount'];
    keys.forEach((key) => {
      if (typeof incoming.dataset[key] !== 'undefined') root.dataset[key] = incoming.dataset[key];
      else delete root.dataset[key];
    });
    root.dataset.loadedDepth = '1';
  }

  async function renderSection(root, rawUrl, options = {}) {
    if (!root) return;
    if (root.dataset.loading === 'true') {
      const previousLoading = state.aborters.get(root);
      if (previousLoading) previousLoading.abort();
    }

    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    const previous = state.aborters.get(root);
    if (previous) previous.abort();
    const controller = new AbortController();
    state.aborters.set(root, controller);

    setLoading(root, true);
    announce(root, collectionText('updating', 'Updating products'));

    try {
      const response = await fetch(cleanSectionUrl(rawUrl, sectionId), {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const incoming = parseIncomingRoot(html);
      if (!incoming) throw new Error('WUB collection section not found');

      root.className = incoming.className;
      root.style.cssText = incoming.style.cssText;
      syncRootData(root, incoming);
      root.innerHTML = incoming.innerHTML;
      setLoading(root, false);

      if (options.push !== false) history.pushState({ wubCollection: true, depth: getDepth(rawUrl) }, '', publicUrl(rawUrl));
      initRoot(root, { restore: options.restore !== false });

      if (options.scroll !== false) {
        const top = root.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: Math.max(0, top), behavior: options.smooth === false ? 'auto' : 'smooth' });
      }

      announce(root, collectionText('updated', 'Products updated'));
      document.dispatchEvent(new CustomEvent('wub:collection-updated', { detail: { sectionId } }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[WUB Collection V2]', error);
        window.location.assign(publicUrl(rawUrl));
      }
    } finally {
      if (state.aborters.get(root) === controller) state.aborters.delete(root);
      if (document.body.contains(root)) setLoading(root, false);
    }
  }

  function urlFromForm(form) {
    const url = new URL(form.action || window.location.href, window.location.origin);
    url.search = '';
    const data = new FormData(form);
    data.forEach((value, key) => {
      const stringValue = String(value).trim();
      if (stringValue !== '') url.searchParams.append(key, stringValue);
    });
    return resetBrowseDepth(url.toString());
  }

  async function loadMore(root, trigger) {
    if (!root || !trigger || trigger.getAttribute('aria-busy') === 'true') return;
    const nextUrl = trigger.href;
    if (!nextUrl) return;

    try {
      await appendPage(root, nextUrl, { trigger });
      const nextUrlObject = new URL(nextUrl, window.location.origin);
      const requestedPage = parseInt(nextUrlObject.searchParams.get('page') || '', 10);
      const currentDepth = Math.max(1, parseInt(root.dataset.loadedDepth || '1', 10) || 1);
      const depth = Number.isFinite(requestedPage) ? requestedPage : currentDepth + 1;
      root.dataset.loadedDepth = String(depth);
      history.pushState({ wubCollection: true, depth }, '', publicUrl(depthStateUrl(depth)));
      announce(root, collectionText('moreLoaded', 'More products loaded'));
      initInfinite(root);
      document.dispatchEvent(new CustomEvent('wub:collection-updated', { detail: { sectionId: root.dataset.sectionId, loadedDepth: depth } }));
    } catch (error) {
      console.error('[WUB Collection V2 load more]', error);
      trigger.removeAttribute('aria-busy');
    }
  }

  function getCollectionContext(root) {
    const url = new URL(window.location.href);
    const filters = [];
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.')) filters.push({ key, value });
    });

    const visibleProducts = [];
    const seen = new Set();
    qsa(root, '[data-wub-product-card] a[href*="/products/"]').forEach((link) => {
      try {
        const productUrl = new URL(link.href, window.location.origin);
        const match = productUrl.pathname.match(/\/products\/([^/?#]+)/);
        if (!match || seen.has(match[1])) return;
        seen.add(match[1]);
        visibleProducts.push({ handle: match[1], variantId: productUrl.searchParams.get('variant') || null });
      } catch (_) {}
    });

    return {
      source: 'collection',
      collectionId: root.dataset.collectionId || null,
      collectionHandle: root.dataset.collectionHandle || null,
      collectionFamily: root.dataset.collectionFamily || 'generic',
      url: publicUrl(window.location.href),
      filters,
      sortBy: url.searchParams.get('sort_by') || null,
      resultCount: parseInt(root.dataset.resultCount || '0', 10) || 0,
      loadedDepth: parseInt(root.dataset.loadedDepth || '1', 10) || 1,
      visibleProducts
    };
  }

  function requestShoppingAssistance(root, action) {
    const event = new CustomEvent('wub:shopping-assistance-requested', {
      bubbles: true,
      cancelable: true,
      detail: getCollectionContext(root)
    });
    action.dispatchEvent(event);
  }

  function updateStickyActions() {
    qsa(document, '[data-wub-collection-actions]').forEach(bar => {
      const reveal = bar.dataset.revealOnScroll === 'true';
      const offset = parseInt(bar.dataset.revealOffset || '0', 10);
      bar.classList.toggle('is-visible', !reveal || window.scrollY >= offset);
    });
  }

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-wub-filter-form]');
    if (!form) return;
    const root = getRoot(form);
    if (!root) return;
    event.preventDefault();
    closeFilters(root);
    renderSection(root, urlFromForm(form));
  });

  document.addEventListener('change', event => {
    const target = event.target;
    if (!target) return;
    const root = getRoot(target);
    if (!root) return;

    if (target.matches('[data-wub-sort]')) {
      const form = document.getElementById(target.getAttribute('form'));
      if (form) form.requestSubmit();
      return;
    }

    if (target.matches('[data-wub-filter-input]')) {
      const form = target.form;
      if (form) form.requestSubmit();
    }
  });

  document.addEventListener('click', event => {
    const cardLink = event.target.closest?.('[data-wub-product-card] a[href*="/products/"]');
    if (cardLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && cardLink.target !== '_blank') {
      saveBrowsePosition(getRoot(cardLink));
    }

    const promoted = event.target.closest?.('[data-wub-promoted-filter-link]');
    if (promoted) {
      const root = getRoot(promoted);
      if (root && promoted.href) {
        event.preventDefault();
        renderSection(root, resetBrowseDepth(promoted.href), { scroll: false });
      }
      return;
    }

    const promotedSummary = event.target.closest?.('.wub-promoted-filter__menu > summary');
    if (promotedSummary) {
      const current = promotedSummary.parentElement;
      const root = getRoot(promotedSummary);
      qsa(root, '.wub-promoted-filter__menu[open]').forEach(menu => {
        if (menu !== current) menu.removeAttribute('open');
      });
      return;
    }

    const open = event.target.closest('[data-wub-filter-open]');
    if (open) {
      const root = getRoot(open);
      if (root) { event.preventDefault(); openFilters(root); }
      return;
    }

    const close = event.target.closest('[data-wub-filter-close]');
    if (close) {
      const root = getRoot(close);
      if (root) { event.preventDefault(); closeFilters(root); }
      return;
    }

    const filterChange = event.target.closest('[data-wub-filter-remove], [data-wub-filter-clear]');
    if (filterChange) {
      const root = getRoot(filterChange);
      if (root && filterChange.href) {
        event.preventDefault();
        renderSection(root, resetBrowseDepth(filterChange.href), { scroll: false });
      }
      return;
    }

    const paginationLink = event.target.closest('[data-wub-pagination-link]');
    if (paginationLink) {
      const root = getRoot(paginationLink);
      if (root && paginationLink.href) {
        event.preventDefault();
        renderSection(root, paginationLink.href);
      }
      return;
    }

    const load = event.target.closest('[data-wub-load-more]');
    if (load) {
      const root = getRoot(load);
      if (root) { event.preventDefault(); loadMore(root, load); }
      return;
    }

    const action = event.target.closest('[data-wub-sticky-action]');
    if (action) {
      const root = getRoot(action);
      if (!root) return;
      if (action.dataset.wubStickyAction === 'filter') {
        event.preventDefault();
        openFilters(root);
      } else if (action.dataset.wubStickyAction === 'assist') {
        event.preventDefault();
        requestShoppingAssistance(root, action);
      }
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    qsa(document, '[data-wub-collection][data-filters-open="true"]').forEach(closeFilters);
    qsa(document, '.wub-promoted-filter__menu[open]').forEach(menu => menu.removeAttribute('open'));
  });

  window.addEventListener('popstate', () => {
    const root = qs(document, '[data-wub-collection]');
    if (root) renderSection(root, window.location.href, { push: false, scroll: false, smooth: false, restore: true });
  });

  window.addEventListener('scroll', () => {
    if (state.scrollTicking) return;
    state.scrollTicking = true;
    requestAnimationFrame(() => {
      updateStickyActions();
      state.scrollTicking = false;
    });
  }, { passive: true });

  document.addEventListener('shopify:section:load', event => {
    const root = qs(event.target, '[data-wub-collection]') || (event.target.matches?.('[data-wub-collection]') ? event.target : null);
    if (root) initRoot(root);
  });

  qsa(document, '[data-wub-collection]').forEach(initRoot);
  updateStickyActions();

  window.WubCollectionV2 = { renderSection, initRoot, getCollectionContext };
})();
